import { useState, useCallback } from 'react';
import { PREGUNTA_TEMPLATE, OPCION_TEMPLATE } from '../constants';

const INITIAL_STATE = {
  nombre: '',
  descripcion: '',
  orden: 1,
  activo: true,
  preguntas: [],
};

export function useCuestionarioForm() {
  const [formData, setFormData] = useState(INITIAL_STATE);

  // Cargar datos del backend (para edición)
  const loadFromApi = useCallback((data) => {
    setFormData({
      nombre: data.nombre,
      descripcion: data.descripcion || '',
      orden: data.orden,
      activo: data.activo,
      preguntas: data.preguntas.map((p) => ({
        id: p.id,
        pregunta: p.pregunta,
        obligatorio: p.obligatorio,
        orden: p.orden,
        activo: p.activo,
        tipo_pregunta: p.tipo_pregunta,
        multi_respuesta: p.multi_respuesta,
        opciones: p.opciones.map((o) => ({
          id: o.id,
          opcion_texto: o.opcion_texto,
          orden: o.orden,
          activo: o.activo,
        })),
      })),
    });
  }, []);

  // Actualizar campo del grupo
  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Agregar pregunta
  const addPregunta = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      preguntas: [
        ...prev.preguntas,
        { ...PREGUNTA_TEMPLATE, orden: prev.preguntas.length + 1 },
      ],
    }));
  }, []);

  // Actualizar pregunta
  const updatePregunta = useCallback((index, field, value) => {
    setFormData((prev) => {
      const preguntas = [...prev.preguntas];
      preguntas[index] = { ...preguntas[index], [field]: value };
      
      // Limpiar opciones si cambia de SELECCION a otro tipo
      if (field === 'tipo_pregunta' && value !== 'SELECCION') {
        preguntas[index].opciones = [];
      }
      return { ...prev, preguntas };
    });
  }, []);

  // Eliminar pregunta (quitarla del array → el PUT la borrará)
  const removePregunta = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      preguntas: prev.preguntas
        .filter((_, i) => i !== index)
        .map((p, i) => ({ ...p, orden: i + 1 })),
    }));
  }, []);

  // Agregar opción a una pregunta
  const addOpcion = useCallback((preguntaIndex) => {
    setFormData((prev) => {
      const preguntas = [...prev.preguntas];
      const opciones = [...preguntas[preguntaIndex].opciones];
      opciones.push({ ...OPCION_TEMPLATE, orden: opciones.length + 1 });
      preguntas[preguntaIndex] = { ...preguntas[preguntaIndex], opciones };
      return { ...prev, preguntas };
    });
  }, []);

  // Actualizar opción
  const updateOpcion = useCallback((preguntaIndex, opcionIndex, field, value) => {
    setFormData((prev) => {
      const preguntas = [...prev.preguntas];
      const opciones = [...preguntas[preguntaIndex].opciones];
      opciones[opcionIndex] = { ...opciones[opcionIndex], [field]: value };
      preguntas[preguntaIndex] = { ...preguntas[preguntaIndex], opciones };
      return { ...prev, preguntas };
    });
  }, []);

  // Eliminar opción
  const removeOpcion = useCallback((preguntaIndex, opcionIndex) => {
    setFormData((prev) => {
      const preguntas = [...prev.preguntas];
      const opciones = preguntas[preguntaIndex].opciones
        .filter((_, i) => i !== opcionIndex)
        .map((o, i) => ({ ...o, orden: i + 1 }));
      preguntas[preguntaIndex] = { ...preguntas[preguntaIndex], opciones };
      return { ...prev, preguntas };
    });
  }, []);

  // Reordenar preguntas (para drag & drop)
  const reorderPreguntas = useCallback((newOrder) => {
    setFormData((prev) => ({
      ...prev,
      preguntas: newOrder.map((p, i) => ({ ...p, orden: i + 1 })),
    }));
  }, []);

  const reset = useCallback(() => setFormData(INITIAL_STATE), []);

  return {
    formData,
    loadFromApi,
    updateField,
    addPregunta,
    updatePregunta,
    removePregunta,
    addOpcion,
    updateOpcion,
    removeOpcion,
    reorderPreguntas,
    reset,
  };
}
